import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** 툴팁이 뜨는 요소: data-tip(+data-kbd)이 있거나, 글자 없는 아이콘 단추 */
const SELECTOR = "[data-tip], .icon-btn[aria-label], .msg-tools button[aria-label]";

interface Tip {
  el: HTMLElement;
  text: string;
  kbd: string | null;
}

/**
 * 화면 전체에 하나: 아이콘 단추에 마우스를 잠깐 올리거나 키보드로 포커스하면 이름과 단축키를 보여준다.
 * 화면 읽기 프로그램은 단추의 aria-label을 읽으므로 이 상자는 보이기만 한다.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ top: -9999, left: -9999 });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const find = (t: EventTarget | null) => (t instanceof Element ? t.closest<HTMLElement>(SELECTOR) : null);
    const show = (el: HTMLElement, delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // 메뉴가 열려 있으면 가리지 않는다
        if (!el.isConnected || el.getAttribute("aria-expanded") === "true") return;
        const text = el.dataset.tip ?? el.getAttribute("aria-label") ?? "";
        if (text) setTip({ el, text, kbd: el.dataset.kbd ?? null });
      }, delay);
    };
    const hide = () => {
      clearTimeout(timer);
      setTip(null);
    };
    const over = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const el = find(e.target);
      if (el) show(el, 450);
    };
    const out = (e: PointerEvent) => {
      const el = find(e.target);
      if (el && !el.contains(e.relatedTarget as Node | null)) hide();
    };
    const focusIn = (e: FocusEvent) => {
      const el = find(e.target);
      if (el?.matches(":focus-visible")) show(el, 0);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && hide();
    document.addEventListener("pointerover", over);
    document.addEventListener("pointerout", out);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("focusin", focusIn);
    document.addEventListener("focusout", hide);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", hide, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerover", over);
      document.removeEventListener("pointerout", out);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("focusin", focusIn);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", hide, true);
    };
  }, []);

  // 요소 위 가운데, 자리가 없으면 아래
  useLayoutEffect(() => {
    if (!tip || !ref.current) return;
    const r = tip.el.getBoundingClientRect();
    const w = ref.current.offsetWidth;
    const h = ref.current.offsetHeight;
    let top = r.top - h - 6;
    if (top < 6) top = r.bottom + 6;
    const left = Math.min(Math.max(6, r.left + r.width / 2 - w / 2), window.innerWidth - w - 6);
    setStyle({ top, left });
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div ref={ref} className="tooltip" aria-hidden="true" style={style}>
      {tip.text}
      {tip.kbd && <kbd>{tip.kbd}</kbd>}
    </div>,
    document.body,
  );
}
